import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { HelpTooltip, Icon, Tooltip } from '~/components';
import getGradientColor from '~/routes/app/pipeline/opportunities/components/gradient';
import { PageLoader } from '~/routes/public/pages';
import { colors, devices, weights } from '~/styles';
import CountUp from './CountUp';
import { estimateRange, formatRange } from './SyncStatusBar';

const REFRESH_DELAY_SECONDS = 5;

const Message = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  color: ${colors.grey55};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: ${weights.light};
`;

const RefreshButton = styled.div`
  margin-left: auto;
  padding-left: 1rem;
  color: ${({ disabled }) => (disabled ? colors.grey25 : colors.black)};
  cursor: pointer;
`;

const Summary = styled.div`
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;

  @media ${devices.touch} {
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  }
`;

const Tile = styled.div`
  position: relative;
  padding: 0.5rem 0.75rem;
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;
`;

const TileLabel = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const TileValue = styled.div`
  margin-top: 0.125rem;
  font-size: 1.25rem;
  font-weight: ${weights.bold};
  white-space: nowrap;
`;

const TileTotal = styled.span`
  margin-left: 0.375rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-weight: ${weights.normal};
`;

const Meter = styled.div`
  height: 0.375rem;
  margin-top: 0.5rem;
  background-color: ${colors.grey10};
  border-radius: 999rem;
`;

const InsideBar = styled.div`
  height: 100%;
  width: ${({ percent }) => percent}%;
  transition: width ${({ durationSeconds }) => durationSeconds}s linear;
  background: ${({ percent }) => getGradientColor(percent)};
  border-radius: 999rem;
`;

export default function ImportedResults({ results, incremental, isLoading, isRunning, refreshSeconds, onRefresh }) {
  const [canRefresh, setCanRefresh] = useState(true);

  const refresh = useCallback(() => {
    if (!canRefresh || isRunning) {
      return;
    }
    setCanRefresh(false);
    setTimeout(() => {
      onRefresh();
      setCanRefresh(true);
    }, REFRESH_DELAY_SECONDS * 1000);
  }, [canRefresh, isRunning, onRefresh]);

  if (isLoading && !results) {
    return <PageLoader />;
  }
  if (!results?.length) {
    return (
      <>
        <TitleRow>
          <Title>Data Import Status</Title>
        </TitleRow>
        <Message>No results available</Message>
      </>
    );
  }

  const measured = results.filter((result) => result.totalHarvest != null);
  const migrated = _.sumBy(measured, 'total');
  const inHarvest = _.sumBy(measured, 'totalHarvest');
  const incrementalRange = incremental?.estimate ? estimateRange(incremental.estimate.estimatedSeconds) : null;

  return (
    <>
      <TitleRow>
        <Title>Data Imported</Title>
        <RefreshButton disabled={!canRefresh || isRunning} onClick={refresh}>
          <Tooltip message={isRunning ? 'Updates automatically during a migration' : 'Refresh results'}>
            <Icon icon="sync" />
          </Tooltip>
        </RefreshButton>
      </TitleRow>
      <Summary>
        <CountUp value={migrated} durationSeconds={refreshSeconds} /> of {inHarvest.toLocaleString()} records imported
        {!isLoading &&
          incremental?.estimate &&
          ` · ${incremental.estimate.recordCount.toLocaleString()} ${
            incremental.estimate.recordCount === 1 ? 'change' : 'changes'
          } since last migration`}
        {!isLoading && incrementalRange && ` · ${formatRange(incrementalRange.low, incrementalRange.high)} to migrate`}
      </Summary>
      <Tiles>
        {results.map((result) => {
          const percent = result.totalHarvest
            ? Math.min(100, Math.round((result.total / result.totalHarvest) * 100))
            : null;

          return (
            <Tile key={result.name}>
              <TileLabel>
                {result.name}
                {result.name === 'Project Team Members' && (
                  <HelpTooltip
                    message="Ruddr adds a person to a project team only when Harvest shows them as the project manager or with time or expenses on that project. Harvest assigns many people to projects by default, so this number is usually lower than the Harvest total."
                    style={{ marginLeft: '.5rem' }}
                  />
                )}
              </TileLabel>
              <TileValue>
                <CountUp value={result.total} durationSeconds={refreshSeconds} />
                <TileTotal>
                  {result.totalHarvest == null ? 'of –' : `of ${result.totalHarvest.toLocaleString()}`}
                </TileTotal>
              </TileValue>
              {percent !== null && (
                <Meter>
                  <InsideBar percent={percent} durationSeconds={refreshSeconds} />
                </Meter>
              )}
            </Tile>
          );
        })}
      </Tiles>
    </>
  );
}
